import Box from 'assets/components/box/Box';
import Button from 'assets/components/button/Button';
import LinkButton from 'assets/components/button/LinkButton';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import Card from 'assets/components/card/Card';
import ThemeCardRow from 'assets/components/card/themes/Theme.Card.Row';
import DataMap from 'assets/components/dataMap/DataMap';
import { addLog } from 'assets/components/feedback/Feedback';
import Icon from 'assets/components/icon/Icon';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import FormWrapper from 'assets/components/inputs/wrappers/FormWrapper';
import Page, { PageClasses, PageStructure } from 'assets/components/page/Page';
import Modal from 'assets/components/page/templates/Modal';
import ThemeModalDefault from 'assets/components/page/templates/themes/Theme.Modal.Default';
import ThemePageSplit from 'assets/components/page/themes/Theme.Page.Split';
import ThemeTagRounded from 'assets/components/tag/themes/Theme.Tag.Rounded';
import useApi from 'assets/hooks/api/useApi';
import { getLocales, Language } from 'assets/locales/Locale';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import Theme from 'assets/themes/Theme.Common';
import { replaceStringTemplateWithValues } from 'assets/utils/data/String';
import { resetScroll } from 'assets/utils/dom/Scroll';
import { createPathFromRoute } from 'assets/utils/dom/UrlParsing';
import { HttpStatus } from 'config/Api.Config';
import { isNull } from 'lodash';
import ContractConsentRequestType from 'models/enums/ContractConsentRequestType';
import ContractStatusCode from 'models/enums/ContractStatusCode';
import VertaforeStatuses from 'models/enums/VertaforeDocumentStatuses';
import productListApi from 'models/productManagement/product/list/Api.Product.List';
import Contract from 'models/sales/contract/Model.Contract';
import contractSetStatusUnrestrictedApi from 'models/sales/contract/setStatusUnrestricted/Api.Contract.SetStatusUnrestricted';
import { ContractRefundAction } from 'module/sales/views/contractRefund/Sales.ContractRefund.Hooks';
import { salesContractRefundIndexRoute } from 'module/sales/views/contractRefund/Sales.ContractRefund.Index';
import { useEffect, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { salesPayAndSignContractIndexRoute } from '../../payAndSignContract/Sales.PayAndSignContract.Index';
import { SalesContractAction, SalesContractInfoTab, useSalesContractProvider } from '../Sales.Contract.Hooks';
import ContractInfoAlerts from './info/Contract.Info.Alerts';
import ContractInfoCancelation from './info/Contract.Info.Cancellation';
import ContractInfoClientHistory from './info/Contract.Info.ClientHistory';
import ContractInfoDocuments from './info/Contract.Info.Documents';
import ContractInfoGeneralInfo from './info/Contract.Info.GeneralInfo';
import ContractInfoNavigation from './info/Contract.Info.Navigation';
import ContractInfoNotes from './info/Contract.Info.Notes';
import ContractInfoRevisions from './info/Contract.Info.Revisions';
import ContractInfoStatusAuditLogs from './info/Contract.Info.StatusAuditLogs';

export default function ContractInfo() {
  const { lang, translate } = getLocales();
  const { isDealer } = useAuthStore();
  const {
    selected,
    pageRouter,
    permissions,
    statuses,
    syncContractWithVertafore,
    syncContractDocumentWithVertafore,
    regenerateContractDocuments,
    listApi,
    findApi,
    contractStatusAuditLogListApi,
    isStatusChanging,
    setIsStatusChanging,
    sendContractConsent,
    contractCancellation,
  } = useSalesContractProvider();

  const productsApi = useApi({
    action: productListApi,
    wait: true,
  });

  const isSignable =
    !!productsApi?.payload?.data
      ?.find((p) => p?.id === (selected?.productId ?? selected?.product?.id))
      ?.productDocuments?.some((pd) => pd?.signatureFields?.length) &&
    selected?.product?.isSendDocumentToSigningEnabled;

  const [regenerateData, setRegenerateData] = useState<{
    contract: Contract;
    languageIsoCode: Locale.Languages;
  }>(undefined);
  const isCustomerConsentRequired = selected?.product?.isCustomerConsentRequired
    ? selected?.contractConsent?.isAccepted === true
    : true;
  const requiresPayment = !selected?.isPayed && selected?.dealer?.isPaymentRequired && isCustomerConsentRequired;
  const requiresSignature =
    !selected?.isSignedManually &&
    !selected?.isSignedWithDocuSing &&
    !selected?.isSignedWithSignFlow &&
    !selected?.isSentForSigning &&
    isSignable;
  isCustomerConsentRequired;
  const canRegenerateContractDocuments =
    selected?.statusCode !== ContractStatusCode.OPEN_WAITING_CONSENT &&
    !(selected?.documentationAggregate || selected?.documentationAggregateId);
  const canResendConsent = isNull(selected?.contractConsent?.isAccepted);
  const canSendConsent = selected?.product?.isCustomerConsentRequired && !selected?.contractConsent;
  //#region Vertafore bool vars
  const isContractSyncable =
    selected?.product?.isStoredInBMS &&
    selected?.statusCode !== ContractStatusCode.CANCELED_VOID &&
    selected?.statusCode !== ContractStatusCode.CANCELED_REFUND_PR_REFUND;

  const isContractDocumentSyncedWithVertafore =
    isContractSyncable &&
    selected?.client?.vertaforeCustomerNumber &&
    (selected?.vertaforeStatus === VertaforeStatuses.NOT_UPLOADED ||
      selected?.vertaforeStatus === VertaforeStatuses.ERROR_UPLOAD_DOCUMENT);

  const isContractSyncedWithVertafore =
    isContractSyncable && !selected?.client?.vertaforeCustomerNumber
      ? selected?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_CLIENT ||
        selected?.vertaforeStatus === VertaforeStatuses.NOT_UPLOADED ||
        selected?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_POLICY
      : selected?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_POLICY;
  //#endregion

  useEffect(() => {
    if (pageRouter.query.infoTab) {
      const container = document.querySelector<HTMLElement>(
        `.${PageStructure.page}.${PageClasses.ThemePageSplit} .${PageStructure.pageSidebar}`
      );
      if (container) resetScroll(container);
    }
  }, [pageRouter.query.infoTab]);

  useEffect(() => {
    productsApi.execute({ productId: selected?.productId ? [selected.productId] : [] });
  }, [selected?.productId]);

  return !selected ? null : (
    <Page
      class={ThemePageSplit}
      isLoading={isStatusChanging}
      header={
        <Card
          class={ThemeCardRow}
          media={<Icon class="if-database" />}
          info={selected?.statusCode ? lang[selected.statusCode]?.replace(/_/g, ' - ') : lang.noContractDefined}
          title={selected?.appNumberWithRevision}
          subtitle={selected?.vehicle?.vin}
          description={
            <>
              {selected?.isPayed && (
                <Box
                  class={ThemeTagRounded}
                  style={{ color: Theme.Color.success, backgroundColor: Theme.Color.successLight }}
                >
                  <Icon class="fas-check" />
                  &nbsp; {lang.paid}
                </Box>
              )}

              {selected?.isContractTransferred && !selected?.isTransferInvisible && (
                <Box
                  class={ThemeTagRounded}
                  style={{ color: Theme.Color.success, backgroundColor: Theme.Color.successLight }}
                >
                  <Icon class="fas-check" />
                  &nbsp; {lang.transferred}
                </Box>
              )}
              {(selected?.isSignedManually ||
                selected?.isSignedWithDocuSing ||
                selected?.isSignedWithSignFlow ||
                selected?.isSentForSigning) && (
                <Box
                  class={ThemeTagRounded}
                  style={{
                    color:
                      selected?.isSignedManually || selected?.isSignedWithDocuSing || selected?.isSignedWithSignFlow
                        ? Theme.Color.success
                        : Theme.Color.warning,
                    backgroundColor:
                      selected?.isSignedManually || selected?.isSignedWithDocuSing || selected?.isSignedWithSignFlow
                        ? Theme.Color.successLight
                        : Theme.Color.warningLight,
                  }}
                >
                  <Icon class="fas-check" />
                  &nbsp;
                  {selected?.isSignedManually
                    ? lang.signedManually
                    : selected?.isSignedWithDocuSing
                      ? lang.signedWithDocuSign
                      : selected?.isSignedWithSignFlow
                        ? lang.signedWithSignFlow
                        : lang.sentForSigning}
                </Box>
              )}
              {!selected?.client?.vertaforeCustomerNumber ? (
                selected?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_CLIENT ? (
                  <Box
                    class={ThemeTagRounded}
                    style={{ color: Theme.Color.error, backgroundColor: Theme.Color.errorLight }}
                  >
                    <Icon class="fas-ban" />
                    &nbsp;
                    {replaceStringTemplateWithValues(lang.syncWithVertaforeStatus, { status: lang.failed })}
                  </Box>
                ) : (
                  selected?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_POLICY && (
                    <Box
                      class={ThemeTagRounded}
                      style={{ color: Theme.Color.error, backgroundColor: Theme.Color.errorLight }}
                    >
                      <Icon class="fas-ban" />
                      &nbsp;
                      {replaceStringTemplateWithValues(lang.syncWithVertaforeStatus, { status: lang.creatingPolicy })}
                    </Box>
                  )
                )
              ) : selected?.vertaforeStatus === VertaforeStatuses.ERROR_UPLOAD_DOCUMENT ? (
                <Box
                  class={ThemeTagRounded}
                  style={{ color: Theme.Color.error, backgroundColor: Theme.Color.errorLight }}
                >
                  <Icon class="fas-ban" />
                  &nbsp;
                  {replaceStringTemplateWithValues(lang.syncedWithVertaforeStatus, {
                    status: lang.documentUploadFailed,
                  })}
                </Box>
              ) : selected?.vertaforeStatus === VertaforeStatuses.NOT_UPLOADED ? (
                <Box
                  class={ThemeTagRounded}
                  style={{ color: Theme.Color.error, backgroundColor: Theme.Color.errorLight }}
                >
                  <Icon class="fas-ban" />
                  &nbsp;
                  {replaceStringTemplateWithValues(lang.syncedWithVertaforeStatus, {
                    status: lang.documentNotUploaded,
                  })}
                </Box>
              ) : selected?.vertaforeStatus === VertaforeStatuses.UNSIGNED_UPLOADED ||
                selected?.vertaforeStatus === VertaforeStatuses.SIGNED_UPLOADED ? (
                <Box
                  class={ThemeTagRounded}
                  style={{ color: Theme.Color.success, backgroundColor: Theme.Color.successLight }}
                >
                  <Icon class="fas-check" />
                  &nbsp; {lang.syncedWithVertafore}
                </Box>
              ) : selected?.vertaforeStatus === VertaforeStatuses.ERROR_CREATE_POLICY ? (
                <Box
                  class={ThemeTagRounded}
                  style={{ color: Theme.Color.error, backgroundColor: Theme.Color.errorLight }}
                >
                  <Icon class="fas-ban" />
                  &nbsp;
                  {replaceStringTemplateWithValues(lang.syncWithVertaforeStatus, { status: lang.creatingPolicy })}
                </Box>
              ) : null}
            </>
          }
        />
      }
      sidebar={
        <>
          {(!pageRouter.query.infoTab || pageRouter.query.infoTab === SalesContractInfoTab.INFO) && (
            <ContractInfoGeneralInfo />
          )}
          {pageRouter.query.infoTab === SalesContractInfoTab.DOCUMENTS && <ContractInfoDocuments />}
          {pageRouter.query.infoTab === SalesContractInfoTab.REVISIONS && <ContractInfoRevisions />}
          {pageRouter.query.infoTab === SalesContractInfoTab.CANCELLATION && <ContractInfoCancelation />}
          {pageRouter.query.infoTab === SalesContractInfoTab.NOTES && <ContractInfoNotes />}
          {pageRouter.query.infoTab === SalesContractInfoTab.CLIENT_HISTORY && <ContractInfoClientHistory />}
          {pageRouter.query.infoTab === SalesContractInfoTab.STATUS_AUDIT_LOGS && <ContractInfoStatusAuditLogs />}
          {pageRouter.query.infoTab === SalesContractInfoTab.ALERTS && <ContractInfoAlerts />}
        </>
      }
      render={<ContractInfoNavigation />}
      footer={
        <>
          {permissions?.EDIT_CONTRACT_UNRESTRICTED && selected?.statusCode === 'OPEN_SUBMITTED' && !isDealer && (
            <Button
              render={lang.editContractUnrestricted}
              class={ThemeButtonCircle}
              onClick={pageRouter.updateParams}
              media={<Icon class="fas-coins" />}
              data={{ id: selected?.id.toString(), action: SalesContractAction.ADJUST_DISTRIBUTIONS }}
            />
          )}
          {permissions?.UPDATE_CONTRACT_STATUS_UNRESTRICTED &&
            selected?.statusCode !== ContractStatusCode.PAID_PENDING_REFUND && (
              <DropdownButton
                buttonMedia={<Icon class="fas-check-double" />}
                horizontalPosition="auto_fixed"
                verticalPosition="auto_fixed"
                subcomponents={ThemeDropdownButtonPopup}
                buttonBody={lang.changeStatus}
                htmlElementProps={{ title: lang.changeStatus }}
                disabled={!permissions?.UPDATE_CONTRACT_STATUS_UNRESTRICTED || !statuses?.length}
                tabIndex={null}
                dropdownOptions={({ animateOut }) => (
                  <DataMap
                    data={statuses}
                    render={({ data: status }) => (
                      <Button
                        key={status.id}
                        onClick={async (newStatus) => {
                          animateOut();

                          setIsStatusChanging(true);
                          const res = await contractSetStatusUnrestrictedApi(selected?.id, {
                            newStatus: newStatus.id as Model.Enum.ContractStatusCode,
                            triggerStatusFlow: false,
                          });
                          if (res.status === HttpStatus.ok) {
                            setIsStatusChanging(false);
                            addLog({ success: lang.contractStatusSuccesfullyChanged });
                          } else {
                            setIsStatusChanging(false);
                            addLog({ error: res.message });
                          }

                          listApi.execute((b) => b);
                          findApi.execute((b) => b);
                          contractStatusAuditLogListApi.execute((b) => b);
                        }}
                        data={status}
                        render={status.title}
                        class={ThemeButtonMenuItem}
                      />
                    )}
                  />
                )}
              />
            )}
          {permissions?.CREATE_CONTRACTREFUND && (
            <Button
              render={lang.prRefund}
              class={ThemeButtonCircle}
              onClick={pageRouter.updateParams}
              media={<Icon class="fas-coins" />}
              disabled={selected?.statusCode !== ContractStatusCode.PAID_ACTIVE}
              data={{ id: selected?.id.toString(), action: SalesContractAction.REFUND }}
            />
          )}
          {permissions?.TRANSFER_CONTRACT &&
            [
              ContractStatusCode.OPEN_SUBMITTED,
              ContractStatusCode.OPEN_UNPAID,
              ContractStatusCode.PAID_ACTIVE,
            ].includes(selected?.statusCode as ContractStatusCode) && (
              <Button
                render={lang.transfer}
                class={ThemeButtonCircle}
                onClick={pageRouter.updateParams}
                media={<Icon class="fas-user" />}
                data={{ id: selected?.id.toString(), action: SalesContractAction.TRANSFER }}
              />
            )}
          {(requiresPayment || requiresSignature) && (
            <LinkButton
              class={ThemeButtonCircle}
              url={createPathFromRoute(
                salesPayAndSignContractIndexRoute,
                {},
                {
                  contractId: selected?.id,
                }
              )}
              media={<Icon class="fas-receipt" />}
              render={requiresPayment && requiresSignature ? 'Pay & sign' : requiresPayment ? lang.pay : lang.sign}
            />
          )}
          {canSendConsent && (
            <DropdownButton
              title={lang.sendConsent}
              buttonMedia={<Icon class="fas-envelope" />}
              horizontalPosition="auto_fixed"
              verticalPosition="auto_fixed"
              constrainment="unconstrained"
              disableWidthConstraint
              subcomponents={ThemeDropdownButtonPopup}
              htmlElementProps={{ title: lang.sendConsent }}
              buttonBody={lang.sendConsent}
              dropdownOptions={
                <>
                  <Button
                    onClick={sendContractConsent}
                    render={lang.email}
                    class={ThemeButtonMenuItem}
                    data={{
                      contract: selected,
                      consentRequestType: ContractConsentRequestType.EMAIL,
                    }}
                  />
                  <Button
                    onClick={sendContractConsent}
                    render={lang.TEXT}
                    class={ThemeButtonMenuItem}
                    data={{
                      contract: selected,
                      consentRequestType: ContractConsentRequestType.TEXT,
                    }}
                  />
                </>
              }
            />
          )}
          {canResendConsent && (
            <Button
              media={<Icon class="fas-envelope" />}
              onClick={pageRouter.updateParams}
              data={{
                id: selected?.id?.toString(),
                action: 'resendConsent',
              }}
              render={lang.resendConsent}
              class={ThemeButtonCircle}
            />
          )}
          {isContractSyncedWithVertafore && (
            <Button
              class={ThemeButtonCircle}
              media={<Icon class="fas-sync" />}
              render={!isMobile && lang.syncContractWithVertafore}
              onClick={syncContractWithVertafore}
              data={{ contractId: selected?.id }}
              disabled={!permissions.VERTAFORE_SYNC_CONTRACT}
            />
          )}
          {isContractDocumentSyncedWithVertafore && (
            <Button
              class={ThemeButtonCircle}
              media={<Icon class="fas-sync" />}
              render={!isMobile && lang.syncContractDocumentationAggregateWithVertafore}
              onClick={syncContractDocumentWithVertafore}
              data={{ contractId: selected?.id }}
              disabled={!permissions.VERTAFORE_SYNC_CONTRACT}
            />
          )}
          {canRegenerateContractDocuments && (
            <>
              <Button
                class={ThemeButtonCircle}
                media={<Icon class="if-refresh" />}
                render={!isMobile && lang.regenerateContractDocuments}
                onClick={setRegenerateData}
                data={{
                  contract: selected,
                  languageIsoCode:
                    selected?.documentLanguageIsoCode ?? selected?.dealer?.documentLanguageIsoCode ?? Language.english,
                }}
                disabled={!permissions.EDIT_CONTRACT}
              />
              {regenerateData && (
                <Modal
                  subcomponents={ThemeModalDefault}
                  onClose={() => setRegenerateData(undefined)}
                  header={lang.regenerateContractDocuments}
                  render={
                    <FormWrapper>
                      <SelectInput
                        name="languageIsoCode"
                        label={lang.language}
                        data={Object.values(Language).map((val) => ({ id: val, title: translate(val) }))}
                        value={regenerateData.languageIsoCode}
                        onChange={({ languageIsoCode }) => setRegenerateData((prev) => ({ ...prev, languageIsoCode }))}
                        icon={<Icon class="fas-language" />}
                      />
                    </FormWrapper>
                  }
                  footer={
                    <Button
                      onClick={() =>
                        regenerateContractDocuments(regenerateData.contract, regenerateData.languageIsoCode)
                      }
                      media="fas-save"
                      render={lang.regenerateContractDocuments}
                      class={ThemeButtonCircle}
                      active
                      htmlElementProps={{ title: lang.regenerateContractDocuments }}
                    />
                  }
                />
              )}
            </>
          )}
          {contractCancellation?.contractRefund && (
            <Button
              class={ThemeButtonCircle}
              onClick={(data) => {
                window.open(
                  createPathFromRoute(salesContractRefundIndexRoute, {
                    ...data,
                  }),
                  '_blank'
                );
              }}
              data={{ id: contractCancellation?.contractRefund?.id?.toString(), action: ContractRefundAction.INFO }}
              media={<Icon class="fas-external-link-square-alt" />}
              style={{ display: 'inline-block' }}
              disabled={
                !(
                  permissions?.VIEW_CONTRACTREFUND ||
                  permissions?.CREATE_CONTRACTREFUND ||
                  permissions?.EDIT_CONTRACTREFUND
                )
              }
            >
              {`${lang.view} ${lang.refundQuote}`}
            </Button>
          )}
        </>
      }
    />
  );
}
